import * as toGeoJSON from '@mapbox/togeojson';


export const convertKMLToGeoJSON = (kmlString) => {
	const parser = new DOMParser();
	const kml = parser.parseFromString(kmlString, 'text/xml');

	return toGeoJSON.kml(kml);
};

export const validateGeoJSON = (content) => {
	try {
		const geojson = typeof content === 'string' ? JSON.parse(content) : content;

		if (!geojson || typeof geojson !== 'object') {
			return {
				valid: false,
				error: 'Invalid GeoJSON'
			};
		}

		// Accept FeatureCollection, Feature or raw Geometry
		let geometry;

		if (geojson.type === 'FeatureCollection') {
			if (!geojson.features?.length) {
				return {
					valid: false,
					error: 'GeoJSON contains no features'
				};
			}

			if (geojson.features.length > 1) {
				return {
					valid: false,
					error: 'GeoJSON must contain a single feature'
				};
			}

			geometry = geojson.features[0]?.geometry;
		} else if (geojson.type === 'Feature') {
			geometry = geojson.geometry;
		} else {
			geometry = geojson;
		}

		if (!geometry) {
			return {
				valid: false,
				error: 'GeoJSON contains no geometry'
			};
		}

		if (geometry.type !== 'Polygon') {
			return {
				valid: false,
				error: 'GeoJSON geometry must be a Polygon'
			};
		}

		const ring = geometry.coordinates?.[0];

		if (!Array.isArray(ring) || ring.length < 4) {
			return {
				valid: false,
				error: 'Polygon must contain at least 4 coordinates'
			};
		}

		const coordinatesValid = ring.every(
			(coord) =>
				Array.isArray(coord) &&
				coord.length >= 2 &&
				Number.isFinite(coord[0]) &&
				Number.isFinite(coord[1])
		);

		if (!coordinatesValid) {
			return {
				valid: false,
				error: 'Polygon contains invalid coordinates'
			};
		}

		const first = ring[0];
		const last = ring[ring.length - 1];

		if (first[0] !== last[0] || first[1] !== last[1]) {
			return {
				valid: false,
				error: 'Polygon is not closed'
			};
		}

		return {
			valid: true,
			geometry
		};
	} catch (error) {
		return {
			valid: false,
			error: 'File is not valid JSON'
		};
	}
};

export const validateKML = (content) => {
	try {
		const parser = new DOMParser();

		const xml = parser.parseFromString(content, 'application/xml');

		// DOMParser returns an XML document containing parsererror
		// rather than throwing for malformed XML
		if (xml.getElementsByTagName('parsererror').length) {
			return {
				valid: false,
				error: 'File contains invalid XML'
			};
		}

		const polygons = xml.getElementsByTagNameNS('*', 'Polygon');

		if (!polygons.length) {
			return {
				valid: false,
				error: 'KML contains no Polygon'
			};
		}

		if (polygons.length > 1) {
			return {
				valid: false,
				error: 'KML must contain a single Polygon'
			};
		}

		const coordinateElement = polygons[0].getElementsByTagNameNS(
			'*',
			'coordinates'
		)[0];

		if (!coordinateElement?.textContent) {
			return {
				valid: false,
				error: 'Polygon contains no coordinates'
			};
		}

		const coordinates = coordinateElement.textContent
			.trim()
			.split(/\s+/)
			.map((coordinate) => {
				const [lng, lat] = coordinate.split(',').map(Number);

				return [lng, lat];
			});

		if (coordinates.length < 4) {
			return {
				valid: false,
				error: 'Polygon must contain at least 4 coordinates'
			};
		}

		const coordinatesValid = coordinates.every(
			([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat)
		);

		if (!coordinatesValid) {
			return {
				valid: false,
				error: 'Polygon contains invalid coordinates'
			};
		}

		const first = coordinates[0];
		const last = coordinates[coordinates.length - 1];

		if (first[0] !== last[0] || first[1] !== last[1]) {
			return {
				valid: false,
				error: 'Polygon is not closed'
			};
		}

		return {
			valid: true,
			geometry: {
				type: 'Polygon',
				coordinates: [coordinates]
			}
		};
	} catch (error) {
		return {
			valid: false,
			error: 'Invalid KML file'
		};
	}
};

export const readUploadedFile = async (file, setUploadedFile) => {
	if (!file) return;
	const extension = file.name.split('.').pop()?.toLowerCase();

	try {
		const fileContents = await file.text();

		setUploadedFile((prev) => ({ ...prev, file: file }));

		if (extension === 'json' || extension === 'geojson') {
			const geojson = validateGeoJSON(fileContents);
			console.log('uploaded geojson', geojson);
			return geojson;
		} else if (extension === 'kml') {
			const geojson = validateKML(fileContents);
			console.log('uploaded kml', geojson);
			return geojson;
		} else {
			setUploadedFile({ file: null, error: 'Unsupported file type' });
			throw new Error('Unsupported file type');
		}
	} catch (e) {
		console.error(e);
		setUploadedFile((prev) => ({
			...prev,
			error: 'There was an error uploading the file'
		}));
	}
};
